import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  searchRecentGraduates, 
  GRADUATE_SOURCING_CRITERIA 
} from '../../services/linkedinIntegrationService';

const GraduateSourcing = () => {
  const { currentUser, hasRole } = useAuth();
  const [graduates, setGraduates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({
    location: '',
    graduationYear: 2024,
    skills: [],
    degree: ''
  });
  const [selectedGraduates, setSelectedGraduates] = useState([]);

  useEffect(() => {
    if (hasRole('admin')) {
      handleSearch();
    }
  }, [hasRole]);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const results = await searchRecentGraduates(searchCriteria);
      setGraduates(results.graduates);
    } catch (error) {
      console.error('Error searching graduates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCriteriaChange = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSkillToggle = (skill) => {
    setSearchCriteria(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleGraduateSelect = (graduateId) => {
    setSelectedGraduates(prev =>
      prev.includes(graduateId)
        ? prev.filter(id => id !== graduateId)
        : [...prev, graduateId]
    );
  };

  const handleBulkAction = (action) => {
    if (selectedGraduates.length === 0) {
      alert('Please select graduates first.');
      return;
    }

    switch (action) {
      case 'export':
        // Export selected graduates
        const selectedData = graduates.filter(g => selectedGraduates.includes(g.id));
        console.log('Exporting graduates:', selectedData);
        alert(`Exporting ${selectedData.length} graduate profiles...`);
        break;
      case 'invite':
        // Invite to APN
        console.log('Inviting graduates to APN:', selectedGraduates);
        alert(`Sending APN invitations to ${selectedGraduates.length} graduates...`);
        break;
      case 'share':
        // Share job opportunities
        console.log('Sharing opportunities with graduates:', selectedGraduates);
        alert(`Sharing opportunities with ${selectedGraduates.length} graduates...`);
        break;
      default:
        break;
    }
  };

  // Admin-only access check
  if (!hasRole('admin')) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">Graduate sourcing is only available to administrators.</p>
          <p className="text-sm text-gray-500">This feature is used for internal talent acquisition and recruitment.</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 bg-apn-blue text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-apn-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold">Graduate Sourcing</h1>
            <p className="mt-2 text-lg">Find and connect with recent finance graduates</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Configuration Notice */}
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Demo Mode</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Graduate sourcing is in demo mode. Full functionality requires LinkedIn API configuration and approval.
                This interface shows the planned features for talent acquisition and recruitment.
              </p>
            </div>
          </div>
        </div>

        {/* Search Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Search Criteria</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={searchCriteria.location}
                onChange={(e) => handleCriteriaChange('location', e.target.value)}
                placeholder="e.g., New York, NY"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
              <select
                value={searchCriteria.graduationYear}
                onChange={(e) => handleCriteriaChange('graduationYear', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              >
                {GRADUATE_SOURCING_CRITERIA.education.graduationYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
              <select
                value={searchCriteria.degree}
                onChange={(e) => handleCriteriaChange('degree', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              >
                <option value="">All Degrees</option>
                {GRADUATE_SOURCING_CRITERIA.education.degrees.map(degree => (
                  <option key={degree} value={degree}>{degree}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleSearch}
                className="w-full bg-apn-blue text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Search
              </button>
            </div>
          </div>

          {/* Skills Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Required Skills</label>
            <div className="flex flex-wrap gap-2">
              {GRADUATE_SOURCING_CRITERIA.skills.map(skill => (
                <button
                  key={skill}
                  onClick={() => handleSkillToggle(skill)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    searchCriteria.skills.includes(skill)
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedGraduates.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800">
                {selectedGraduates.length} graduate(s) selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleBulkAction('export')}
                  className="text-sm bg-white text-blue-800 px-3 py-1 rounded border border-blue-300 hover:bg-blue-50"
                >
                  Export
                </button>
                <button
                  onClick={() => handleBulkAction('invite')}
                  className="text-sm bg-white text-blue-800 px-3 py-1 rounded border border-blue-300 hover:bg-blue-50"
                >
                  Invite to APN
                </button>
                <button
                  onClick={() => handleBulkAction('share')}
                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                >
                  Share Opportunities
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Graduates ({graduates.length})
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSelectedGraduates(graduates.map(g => g.id))}
                  className="text-sm text-apn-blue hover:text-blue-700"
                >
                  Select All
                </button>
                <button
                  onClick={() => setSelectedGraduates([])}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-apn-blue"></div>
              </div>
            ) : graduates.length > 0 ? (
              <div className="space-y-4">
                {graduates.map((graduate) => (
                  <div
                    key={graduate.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      selectedGraduates.includes(graduate.id)
                        ? 'border-apn-blue bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedGraduates.includes(graduate.id)}
                          onChange={() => handleGraduateSelect(graduate.id)}
                          className="mt-1 h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium text-gray-900">{graduate.name}</h3>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Available
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {graduate.degree} • {graduate.university} • Class of {graduate.graduationYear}
                          </p>
                          <p className="text-sm text-gray-500">{graduate.location}</p>
                          
                          <div className="mt-2">
                            <div className="flex flex-wrap gap-1">
                              {graduate.skills.map(skill => (
                                <span
                                  key={skill}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>

                          {graduate.internships.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500">Previous Experience:</p>
                              <p className="text-sm text-gray-700">{graduate.internships.join(', ')}</p>
                            </div>
                          )}

                          <div className="mt-2">
                            <p className="text-xs text-gray-500">Interested in:</p>
                            <p className="text-sm text-gray-700">{graduate.preferredRoles.join(', ')}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <a
                          href={graduate.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm bg-apn-blue text-white px-3 py-1 rounded hover:bg-blue-700"
                        >
                          View LinkedIn
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No graduates found</h3>
                <p className="text-gray-600">Try adjusting your search criteria to find more candidates.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraduateSourcing;
